import { Injectable } from '@angular/core';
import { RestfulApiService } from './restful-api.service';
import {  Observable } from 'rxjs';

export interface MediaObject {
  uuid: string;
  url: string;
  filename: string;
  type: string;
}

export interface ChampionLinkedEntityData {
  uuid: string | null;
  entityName: string | null;
  firstName: string;
  lastName: string;
  email: string | null;
  coverImage: string | null;
  dueDiligenceLevel: string | null;
  grantsAwardedNumber: string | null;
  currency: string | null;
  document: string | null;
  video: MediaObject | null;
  photo: MediaObject | null;
  quote: string | null;
  history: string | null;
  impactStory: string | null;
  topRisks: string | null;
  championTestimonialUuid: string | null;
  testimonial: string;
  engagementYear: number;
  engagementMonth: number;
  yearsOfEngagement: number | null;
  validityYear: number;
  validityMonth: number;
  photoOrVideo: MediaObject | null;
}

export interface CharityChampionDraftResponse {
  response: {
    organisationUuid: string;
    organisationChampionLinkUuid: string;
    organisationName: string;
    championLinkedEntityData: ChampionLinkedEntityData;
  };
}

export interface OrganisationSortBy {
  field: 'ORGANISATION_NAME' | string;
  order: 'ASC' | 'DESC';
  checked: boolean;
}

export interface GetProjectsPayload {
  firstRequest: boolean;
  page: number;
  size: number;
  searchText: string;
  sortBy: OrganisationSortBy;
  statusList: string[];
  projectStatusList: string[];
  /**
   * Selected charity UUIDs for server-side charity filtering. Only sent by
   * foundation / super admins filtering by charity; omitted for other roles
   * (which filter charities client-side).
   */
  charityUuids?: string[];
  /**
   * True when the list is filtered by charity and the synthetic "All Charities"
   * option is selected — the backend then returns projects from every charity and
   * `charityUuids` is omitted. False in every other case (including individual
   * charity selections). Mirrors `GetUsersPayload.allCharities`.
   */
  allCharities: boolean;
}

export interface ProjectListItem {
  uuid: string;
  name: string;
  entityImageUrl: string;
  entityStatus: string;
  organisationUuid: string;
  organisationName: string;
  language: {
    uuid: string;
    code: string;
    name: string;
    flag: string;
    primaryLanguage: boolean;
  };
  changeStatusReason: string | null;
  description: string | null;
  // Extra fields returned by the backend for the GS_ORGANIZATION_ADMIN / GS_PROJECT_MANAGER role
  cause?: {
    uuid: string;
    name: string;
  } | null;
  projectCategories?: Array<{
    uuid: string;
    name: string;
  }>;
  // Funding fields (being added by the backend; optional until the response includes them).
  // The *CurrencySymbol fields carry a currency code (e.g. 'USD'), used as the conversion source.
  amountRaisedToDate?: number;
  amountRaisedToDateCurrencySymbol?: string;
  totalProjectBudget?: number;
  totalProjectBudgetCurrencySymbol?: string;
}

export interface GetProjectsResponse {
  response: {
    pageNumber: number;
    totalObjects: number;
    from: number;
    to: number;
    objects: ProjectListItem[];
  };
}

export interface ToggleProjectVisiblePayload {
  uuid: string;
  entityStatus: string;
  visible: boolean;
  scheduled: boolean;
  time?: number;
  reason?: string;
}

export interface ToggleProjectVisibleResponse {
  response: {
    uuid: string | null;
    entityStatus: string;
    scheduled: boolean;
    time: string | null;
    reason: string | null;
    projectsLanguages: ToggleProjectLanguage[] | null;
  };
}

export interface GetOrganisationsPayload {
  firstRequest: boolean;
  page: number;
  size: number;
  searchText: string;
  sortBy: OrganisationSortBy;
  statusList: string[];
  projectStatusList: string[];
}

export interface OrganisationListItem {
  uuid: string;
  name: string;
  entityImageUrl: string;
  entityStatus: string;
  language: {
    uuid: string;
    code: string;
    name: string;
    flag: string;
    primaryLanguage: boolean;
  };
  changeStatusReason: string | null;
  description: string | null;
  // Extra fields returned by the backend for the GS_ORGANIZATION_ADMIN role
  address?: {
    uuid?: string;
    city: string;
    country: string;
    countryCode?: string;
    stateProvince?: string | null;
    streetName?: string;
    streetNumber?: string | null;
    postalCode?: string;
    landmarkAddress?: string | null;
    latitude?: number | null;
    longitude?: number | null;
  } | null;
  cause?: {
    uuid: string;
    name: string;
  } | null;
  categories?: Array<{
    uuid: string;
    name: string;
  }>;
  championBadges?: Array<{
    uuid: string;
    name: string;
    rank: number;
    image?: {
      uuid?: string;
      url: string;
      filename?: string;
      type?: string;
      position?: number | null;
      fileSize?: number;
    } | null;
  }>;
}

export interface GetOrganisationsResponse {
  response: {
    pageNumber: number;
    totalObjects: number;
    from: number;
    to: number;
    objects: OrganisationListItem[];
  };
}

export interface ToggleOrganisationVisiblePayload {
  uuid: string;
  entityStatus: string;
  reason?: string;
}

export interface ToggleOrganisationLanguage {
  uuid: string;
  code: string;
  name: string;
  flag: string;
  entityUuid: string;
  entityStatus: string;
  primaryLanguage: boolean;
}

export interface ToggleProjectLanguage {
  uuid: string;
  code: string;
  name: string;
  flag: string;
  entityUuid: string;
  entityStatus: string;
  primaryLanguage: boolean;
}

export interface ToggleOrganisationVisibleResponse {
  response: {
    uuid: string | null;
    entityStatus: string;
    scheduled: boolean;
    time: string | null;
    reason: string | null;
    organisationLanguages: ToggleOrganisationLanguage[] | null;
    projectsLanguages: ToggleProjectLanguage[] | null;
  };
}

export interface OrganisationDropdownItem {
  uuid: string;
  name: string;
  imageUrl?: string | null;
}

/**
 * The `organisations` array on `GET /v1/organisation/dropdowns` carries the charity's own status
 * (be-resource-server `OrganisationDropdownOption`, GIV-5504) — no other dropdown list on that
 * response does, so this is a dedicated type rather than adding the field to the shared
 * `OrganisationDropdownItem` every other list also uses.
 */
export interface CharityDropdownItem extends OrganisationDropdownItem {
  status?: string;
}

export interface RegionDropdownItem extends OrganisationDropdownItem {
  countriesUuids: string[] | null;
}

export interface RegionOrderItem {
  uuid: string;
  position: number;
}

export interface OrganisationDropdownsResponse {
  categories: OrganisationDropdownItem[];
  currencies: OrganisationDropdownItem[];
  countries: OrganisationDropdownItem[];
  regions: RegionDropdownItem[];
  champions: OrganisationDropdownItem[];
  causes: OrganisationDropdownItem[];
  budgetYears: OrganisationDropdownItem[];
  projectManagers: OrganisationDropdownItem[];
  organisations?: CharityDropdownItem[];
}

export interface OrganisationProfile {
  uuid: string;
  name: string;
  numberOfEntities: number;
  default: boolean;
}

export interface OrganisationProfilesResponse {
  response: OrganisationProfile[];
}

export interface OrganisationAddress {
  streetName: string;
  streetNumber: string | null;
  city: string;
  country: string;
  countryCode: string | null;
  latitude: number | null;
  longitude: number | null;
  stateProvince: string;
  postalCode: string;
  landmarkAddress: string | null;
}

export interface OrganisationMediaDto {
  fileType: string;
  allowedFileType: string;
  uuid?: string;
  fileName?: string;
  url?: string;
  objectUuid?: string;
  cover: boolean;
  pendingUpload: boolean;
  isFormatValid: boolean;
  progress: number;
  position?: number;
}

export interface OrganisationImageItem {
  mediaDto: OrganisationMediaDto;
  position: number;
}

export interface OrganisationTeamMember {
  uuid?: string;
  position: number;
  fullName: string;
  profilePicture: OrganisationMediaDto;
  bio: string;
  linkedinProfile: string;
  email: string;
}

export interface OrganisationPartner {
  uuid?: string;
  position: number;
  name: string;
  relationshipDescription: string;
  email: string;
}

export interface OrganisationBudget {
  budgetRevenues: any[];
  budgetExpenses: any[];
  budgetYearUUID: string;
  fiscalYearEnd: string;
  totalActualsExpenses: number;
  totalActualsRevenues: number;
  totalBudgetExpenses: number;
  totalBudgetRevenues: number;
  netGainLossBudget: number;
  netGainLossActuals: number;
  uuid?: string;
  budgetCurrency: OrganisationDropdownItem;
  budgetYear: OrganisationDropdownItem;
  position: number;
}

export interface CreateUpdateOrganisationRequest {
  organisationUuid?: string | null;
  mandatoryProfileUuid: string;
  organisationName: string;
  isPrivate: boolean;
  address: OrganisationAddress;
  organisationWebsite: string;
  socialMediaLinkedin: string | null;
  socialMediaTwitter: string | null;
  socialMediaFacebook: string | null;
  socialMediaInstagram: string | null;
  cause: OrganisationDropdownItem | null;
  organisationCategories: OrganisationDropdownItem[];
  targetPopulation: string | null;
  organisationInfluencedCountries: OrganisationDropdownItem[];
  globalInfluence: boolean;
  regionOrder: RegionOrderItem[];
  yearFounded: number | null;
  givingLink: string;
  organisationVideo: OrganisationMediaDto | null;
  organisationImages: OrganisationImageItem[];
  executiveSummary: string;
  visionStatement: string | null;
  missionStatement: string | null;
  organisationValues: string | null;
  programAndActivities: string;
  history: string | null;
  organisationLeader: OrganisationTeamMember | null;
  modifiedFields: Record<string, any>;
  numberOfEmployees: number;
  numberOfVolunteers: number;
  executiveTeams: OrganisationTeamMember[];
  boardMembers: OrganisationTeamMember[];
  partnerOrganisations: OrganisationPartner[];
  budgetOrganisations: OrganisationBudget[];
  submitted: boolean;
  languageUuid: string;
  organisationDocuments: any[];
}

export interface CreateUpdateOrganisationResponse {
  response: {
    uuid: string;
    submitted: boolean;
  };
}

@Injectable({
  providedIn: 'root',
})
export class OrganizationProjectService {
  private readonly organizationResourceUrl: string = '/v1/organisation';
  private readonly projectResourceUrl: string = '/v1/project';

  constructor(private apiService: RestfulApiService) {}

  getOrganizationDetails(uuid: string, languageCode: string, preview: boolean, globalSearch?: boolean): Observable<any> {
    const params = languageCode ? { languageCode, preview, globalSearch } : { preview, globalSearch };
    return this.apiService.get(`${this.organizationResourceUrl}/details/${uuid}`, params);
  }

  championCharity(
    entityUuid: string,
    organisationChampionLinkUuid: string,
    champion: {
      organizationEngagement: {
        engagementStartMonth: { uuid: string };
        engagementStartYear: { uuid: string };
      };
      photoOrVideo: {
        uuid: string | null;
      };
      testimonial: string;
      testimonialVisibility: { uuid: string };
    }
  ): Observable<any> {
    const payload = {
      organisationUuid: entityUuid,
      organisationChampionLinkUuid,
      championLinkedEntityData: {
        engagementMonth: +champion.organizationEngagement.engagementStartMonth.uuid,
        engagementYear: +champion.organizationEngagement.engagementStartYear.uuid,
        photoOrVideo: {
          uuid: champion.photoOrVideo.uuid,
        },
        testimonial: champion.testimonial,
        uuid: null,
        validityMonth: +champion.testimonialVisibility.uuid,
      },
    };
    return this.apiService.post('/v1/champion/link-organisation-submit', payload);
  }

  saveChampionCharityDraft(
    entityUuid: string,
    organisationChampionLinkUuid: string,
    champion: {
      organizationEngagement: {
        engagementStartMonth: { uuid: string };
        engagementStartYear: { uuid: string };
      };
      photoOrVideo: {
        uuid: string | null;
      };
      testimonial: string;
      testimonialVisibility: { uuid: string };
    }
  ): Observable<any> {
    const payload = {
      organisationUuid: entityUuid,
      organisationChampionLinkUuid,
      championLinkedEntityData: {
        engagementMonth: +champion.organizationEngagement.engagementStartMonth.uuid,
        engagementYear: +champion.organizationEngagement.engagementStartYear.uuid,
        photoOrVideo: {
          uuid: champion.photoOrVideo.uuid,
        },
        testimonial: champion.testimonial,
        uuid: null,
        validityMonth: +champion.testimonialVisibility.uuid,
      },
    };
    return this.apiService.post('/v1/champion/link-organisation-save', payload);
  }

  getCharityChampionDraft(uuid: string, uuid2: string): Observable<CharityChampionDraftResponse> {
    const payload = { uuid, uuid2 };
    return this.apiService.post('/v1/champion/get-testimonial-draft', payload) as Observable<CharityChampionDraftResponse>;
  }

  getOrganisations(payload: GetOrganisationsPayload): Observable<GetOrganisationsResponse> {
    return this.apiService.post('/v1/organisation/get-organisations', payload) as Observable<GetOrganisationsResponse>;
  }

  deleteOrganisation(uuid: string, languageCode: string): Observable<{ response: string }> {
    return this.apiService.delete(`${this.organizationResourceUrl}/${uuid}/delete`, { languageCode }) as Observable<{ response: string }>;
  }

  toggleOrganisationVisible(payload: ToggleOrganisationVisiblePayload, languageCode: string): Observable<ToggleOrganisationVisibleResponse> {
    return this.apiService.post(`${this.organizationResourceUrl}/toggle-visible`, payload, { languageCode }) as Observable<ToggleOrganisationVisibleResponse>;
  }

  /**
   * Get organisation dropdowns for form fields
   * Endpoint: GET /v1/organisation/dropdowns
   * Returns dropdown options for categories, countries, regions, causes, currencies, champions, budgetYears, etc.
   */
  getOrganisationDropdowns(): Observable<OrganisationDropdownsResponse> {
    return this.apiService.get(`${this.organizationResourceUrl}/dropdowns`) as Observable<OrganisationDropdownsResponse>;
  }

  /**
   * Get all organisation profiles
   * Endpoint: GET /v1/profiles/get-all/ORGANISATION
   * Returns list of organisation profiles with uuid, name, numberOfEntities, and default flag
   */
  getOrganisationProfiles(): Observable<OrganisationProfilesResponse> {
    return this.apiService.get('/v1/profiles/get-all/ORGANISATION') as Observable<OrganisationProfilesResponse>;
  }

  /**
   * Create or update an organisation
   * Endpoint: POST /v1/organisation/create-update
   * Creates a new organisation or updates an existing one
   */
  createOrUpdateOrganisation(payload: CreateUpdateOrganisationRequest): Observable<CreateUpdateOrganisationResponse> {
    return this.apiService.post(`${this.organizationResourceUrl}/create-update`, payload) as Observable<CreateUpdateOrganisationResponse>;
  }

  /**
   * Get organisation data for editing
   * Endpoint: GET /v1/organisation/{orgUuid}?languageCode={code}
   * Fetches complete organization data for editing
   */
  getOrganisationForEdit(uuid: string, languageCode: string): Observable<any> {
    return this.apiService.get(`${this.organizationResourceUrl}/${uuid}`, { languageCode });
  }

  /**
   * Get organisation dropdowns with organization context
   * Endpoint: GET /v1/organisation/dropdowns?organisationUuid={uuid}
   * Fetches organization-specific dropdowns
   */
  getOrganisationDropdownsWithContext(organisationUuid: string): Observable<any> {
    return this.apiService.get(`${this.organizationResourceUrl}/dropdowns`, { organisationUuid });
  }

  /**
   * Get paginated list of projects
   * Endpoint: POST /v1/project/get-projects
   */
  getProjects(payload: GetProjectsPayload): Observable<GetProjectsResponse> {
    return this.apiService.post(`${this.projectResourceUrl}/get-projects`, payload) as Observable<GetProjectsResponse>;
  }

  /**
   * Delete a project
   * Endpoint: DELETE /v1/project/delete/{uuid}
   */
  deleteProject(uuid: string, languageCode: string): Observable<{ response: string }> {
    return this.apiService.delete(`${this.projectResourceUrl}/delete/${uuid}`, { languageCode }) as Observable<{ response: string }>;
  }

  /**
   * Toggle project visibility/status
   * Endpoint: POST /v1/project/toggle-visible
   */
  toggleProjectVisible(payload: ToggleProjectVisiblePayload, languageCode: string): Observable<ToggleProjectVisibleResponse> {
    return this.apiService.post(`${this.projectResourceUrl}/toggle-visible`, payload, { languageCode }) as Observable<ToggleProjectVisibleResponse>;
  }

  /**
   * Get organisation projects and available languages
   * Endpoint: GET /v1/project/get-organisation-projects/{orgUuid}
   * Returns organization's projects and available languages for editing
   */
  getOrganisationProjects(orgUuid: string): Observable<any> {
    return this.apiService.get(`/v1/project/get-organisation-projects/${orgUuid}`);
  }

  /**
   * Save organisation configuration (ranking, profile)
   * Endpoint: POST /v1/organisation/save-configuration/{organisationUuid}
   */
  saveOrganisationConfiguration(organisationUuid: string, payload: { mandatoryProfileUuid: string; rank: number }): Observable<any> {
    return this.apiService.post(`${this.organizationResourceUrl}/save-configuration/${organisationUuid}`, payload);
  }

  editProjectScore(projectUuid: string, score: number): Observable<any> {
    return this.apiService.post(`${this.projectResourceUrl}/edit-score`, { score, uuid: projectUuid });
  }

  checkOrganisationName(name: string, uuid: string | null): Observable<{ response: boolean }> {
    return this.apiService.post(`${this.organizationResourceUrl}/check-name`, { name, uuid }) as Observable<{ response: boolean }>;
  }
}
